<template>
    <!-- Teleport the breadcrumb to the App.vue header breadcrumb location -->
    <Teleport v-if="isReady && targetExists" to="#breadcrumb-container">
        <DynamicBreadcrumb :class="class">
            <template #active-suffix="{ item }">
                <slot name="breadcrumb-suffix" :item="item" />
            </template>
            <template #icon="{ item }">
                <slot name="breadcrumb-icon" :item="item">
                    <component v-if="item.icon" :is="item.icon" class="h-4 w-4" />
                </slot>
            </template>
            <template #label="{ item }">
                <slot name="breadcrumb-label" :item="item">
                    <span>{{ item.label }}</span>
                </slot>
            </template>
        </DynamicBreadcrumb>
    </Teleport>
    
    <!-- Teleport actions to the App.vue header actions slot -->
    <Teleport v-if="isReady && actionsTargetExists && $slots.actions" to="#page-breadcrumb-actions-slot">
        <slot name="actions" />
    </Teleport>
</template>

<script setup>
import { provide, onMounted, onUnmounted, ref, nextTick, onBeforeUnmount, useSlots, watch, computed } from 'vue';
import DynamicBreadcrumb from './DynamicBreadcrumb.vue';

/**
 * @typedef Props
 * @property {string} [class] - CSS classes to apply to the breadcrumb
 */
const props = defineProps({
    class: {
        type: String,
        default: ''
    }
});

// Disable the main breadcrumb when PageBreadcrumb is used
const BREADCRUMB_DISABLED_KEY = Symbol('breadcrumb-disabled');

// Track component state
const isReady = ref(false);
const targetExists = ref(false);
const actionsTargetExists = ref(false);
const mounted = ref(false);
const slots = useSlots();

const checkTargets = () => {
    if (!mounted.value) return;
    
    const breadcrumbContainer = document.querySelector('#breadcrumb-container');
    const actionsSlot = document.querySelector('#page-breadcrumb-actions-slot');
    
    targetExists.value = !!breadcrumbContainer;
    actionsTargetExists.value = !!actionsSlot;
    
    // Ensure App.vue knows breadcrumb is active when targets exist
    if (breadcrumbContainer && actionsSlot) {
        window.__pageBreadcrumbActive = true;
    }
};

onMounted(async () => {
    mounted.value = true;
    
    // Provide a signal that page-level breadcrumb is active
    provide(BREADCRUMB_DISABLED_KEY, true);
    
    // Wait for the next tick to ensure DOM updates
    await nextTick();
    
    // Check if targets exist first, THEN set the global flag
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds max (100 attempts × 100ms = 10s)
    
    const waitForTargets = () => {
        checkTargets();
        
        if (targetExists.value || attempts >= maxAttempts) {
            isReady.value = true;
            
            // Only set the global flag AFTER we're ready to teleport
            window.__pageBreadcrumbActive = true;
            
            // Trigger immediate check in parent App.vue
            window.dispatchEvent(new CustomEvent('pageBreadcrumbChanged'));
            return;
        }
        
        attempts++;
        setTimeout(waitForTargets, 100);
    };
    
    waitForTargets();
    
    // Setup DOM mutation observer as backup
    if (!targetExists.value) {
        setupMutationObserver();
    }
    
    // Additional fallback: keep checking periodically if teleport failed
    const fallbackCheck = setInterval(() => {
        if (!targetExists.value && mounted.value) {
            checkTargets();
            if (targetExists.value) {
                isReady.value = true;
                window.__pageBreadcrumbActive = true;
                window.dispatchEvent(new CustomEvent('pageBreadcrumbChanged'));
                clearInterval(fallbackCheck);
                mutationObserver?.disconnect();
            }
        } else if (targetExists.value) {
            clearInterval(fallbackCheck);
            mutationObserver?.disconnect();
        }
    }, 1000);
    
    // Clean up fallback after 30 seconds
    setTimeout(() => {
        clearInterval(fallbackCheck);
        mutationObserver?.disconnect();
    }, 30000);
});

let mutationObserver = null;

const setupMutationObserver = () => {
    // Watch for DOM changes that might add the breadcrumb targets
    if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
            if (!targetExists.value && mounted.value) {
                checkTargets();
                if (targetExists.value) {
                    isReady.value = true;
                    window.__pageBreadcrumbActive = true;
                    window.dispatchEvent(new CustomEvent('pageBreadcrumbChanged'));
                    mutationObserver?.disconnect();
                }
            }
        });
        
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false
        });
    }
};

onBeforeUnmount(() => {
    mounted.value = false;
    isReady.value = false;
    mutationObserver?.disconnect();
});

onUnmounted(() => {
    // Clean up when component is destroyed
    window.__pageBreadcrumbActive = false;
    targetExists.value = false;
    actionsTargetExists.value = false;
    mutationObserver?.disconnect();
    
    // Trigger immediate check in parent App.vue to minimize race condition
    window.dispatchEvent(new CustomEvent('pageBreadcrumbChanged'));
});

// Watch for changes in teleport conditions
const shouldTeleportActions = computed(() => isReady.value && actionsTargetExists.value && !!slots.actions);

watch(shouldTeleportActions, (newValue) => {
    // Actions teleport condition changed - could add analytics here if needed
});

// Export the key so other components can check for it
// export { BREADCRUMB_DISABLED_KEY };
</script>